import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { FORM } from "@/constants/testIds";
import { toast } from "sonner";
import { Check, ArrowUpRight } from "lucide-react";

export default function LeadForm({ leadType, sourcePage, title, subtitle, fields = ["name","email","phone","message"], extra = [], submitLabel = "Send" }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSubmitting(true);
    try {
      await api.post("/public/leads", {
        lead_type: leadType,
        source_page: sourcePage,
        consent: true,
        ...form,
      });
      setSent(true);
      toast.success("Thank you. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Submission failed"));
    } finally { setSubmitting(false); }
  };

  if (sent) {
    return (
      <div className="surface-paper p-12 lg:p-16 border-t-2 border-ruby" data-testid={`${leadType}-form-sent`}>
        <div className="w-14 h-14 border border-ruby flex items-center justify-center">
          <Check className="text-ruby" size={22} strokeWidth={1.3} />
        </div>
        <p className="label-mono mt-10">Received</p>
        <h3 className="serif-display text-5xl lg:text-6xl text-ink-700 mt-6">Thank you.</h3>
        <p className="text-ink-400 mt-6 leading-[1.8] max-w-md text-base lg:text-lg">
          A member of the Rubys.Group team will be in touch within one working day.
        </p>
      </div>
    );
  }

  const Field = ({ name, label, type = "text", textarea = false, options }) => (
    <label className="block">
      <span className="label-mono-mute">{label}</span>
      {textarea ? (
        <textarea
          value={form[name] || ""}
          onChange={onChange(name)}
          rows={4}
          data-testid={FORM.field(`${leadType}-${name}`)}
          className="editorial-input mt-2 resize-none"
        />
      ) : options ? (
        <select
          value={form[name] || ""}
          onChange={onChange(name)}
          data-testid={FORM.field(`${leadType}-${name}`)}
          className="editorial-input mt-2 appearance-none"
        >
          <option value="">Select…</option>
          {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={form[name] || ""}
          onChange={onChange(name)}
          data-testid={FORM.field(`${leadType}-${name}`)}
          className="editorial-input mt-2"
        />
      )}
    </label>
  );

  const fieldDefs = {
    name: { name: "name", label: "Name *" },
    email: { name: "email", label: "Email *", type: "email" },
    phone: { name: "phone", label: "Phone" },
    company: { name: "company", label: "Company" },
    property_address: { name: "property_address", label: "Property Address" },
    property_details: { name: "property_details", label: "Property Details", textarea: true },
    budget: { name: "budget", label: "Budget" },
    message: { name: "message", label: "Message", textarea: true },
  };

  return (
    <form onSubmit={submit} className="surface-paper p-8 md:p-12 lg:p-14 border-t-2 border-ruby space-y-10" data-testid={`lead-form-${leadType}`}>
      {title && <div>
        <p className="label-mono">{leadType.replace(/_/g, " ")}</p>
        <h3 className="serif-display text-4xl lg:text-5xl text-ink-700 mt-4">{title}</h3>
        {subtitle && <p className="text-ink-100 mt-5 leading-[1.7] max-w-md">{subtitle}</p>}
      </div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        {fields.filter((f) => fieldDefs[f] && !fieldDefs[f].textarea).map((f) => <Field key={f} {...fieldDefs[f]} />)}
      </div>
      <div className="space-y-8">
        {extra.map((ex) => <Field key={ex.name} {...ex} />)}
        {fields.filter((f) => fieldDefs[f]?.textarea).map((f) => <Field key={f} {...fieldDefs[f]} />)}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-ruby-solid disabled:opacity-50"
        data-testid={FORM.submit(leadType)}
      >
        {submitting ? "Sending…" : submitLabel} <ArrowUpRight size={14} />
      </button>
    </form>
  );
}
